import { Fragment, useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth, db } from './firebaseConfig';
import { F, R, T, THEME_CSS, useTheme } from './theme';
import { Icon, Logo, PanelErrorBoundary, useIsMobile } from './ui';
import { ProfileViewerContext, UserProfileModal } from './profile';
import { BookingViewerContext } from './bookingViewer';
import { BookingModal } from './panels/Bookings';

// Panels load on first visit rather than in the initial bundle: the admin
// panel is eleven screens, and opening the Dashboard should not pay to
// download the carousel editor or the settings page.
const Dashboard = lazy(() => import('./panels/Dashboard').then(m => ({ default: m.Dashboard })));
const Analytics = lazy(() => import('./panels/Analytics').then(m => ({ default: m.Analytics })));
const Users = lazy(() => import('./panels/Users').then(m => ({ default: m.Users })));
const Bikes = lazy(() => import('./panels/Bikes').then(m => ({ default: m.Bikes })));
const Bookings = lazy(() => import('./panels/Bookings').then(m => ({ default: m.Bookings })));
const Reports = lazy(() => import('./panels/Reports').then(m => ({ default: m.Reports })));
const Payouts = lazy(() => import('./panels/Payouts').then(m => ({ default: m.Payouts })));
const Broadcast = lazy(() => import('./panels/Broadcast').then(m => ({ default: m.Broadcast })));
const Carousel = lazy(() => import('./panels/Carousel').then(m => ({ default: m.Carousel })));
const AppSettings = lazy(() => import('./panels/AppSettings').then(m => ({ default: m.AppSettings })));
const Reviews = lazy(() => import('./panels/Reviews').then(m => ({ default: m.Reviews })));
const Boosts = lazy(() => import('./panels/Boosts').then(m => ({ default: m.Boosts })));
const Coupons = lazy(() => import('./panels/Coupons').then(m => ({ default: m.Coupons })));
const Locations = lazy(() => import('./panels/Locations').then(m => ({ default: m.Locations })));

const ADMIN_EMAIL = 'admin@saikapp.com';

// ── Theme ────────────────────────────────────────────────
// Every token resolves to a CSS custom property, so switching light/dark is a
// single `data-theme` swap on <html> — no React re-render, no prop drilling,
// and all inline styles below keep referencing T.* unchanged.

function Login() {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(auth, ADMIN_EMAIL, password);
    } catch (e) {
      if (e.code === 'auth/operation-not-allowed') {
        setError('Email/Password sign-in is disabled in Firebase Auth settings.');
      } else if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        setError('Incorrect password. Try again.');
      } else if (e.code === 'auth/user-not-found') {
        setError('Admin account not found. Check it was created correctly.');
      } else {
        setError(`Login failed: ${e.code || e.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: T.bg, padding: 20,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{
          backgroundColor: T.card, border: `1px solid ${T.cardBorder}`,
          borderRadius: 18, padding: 32,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          boxShadow: T.shadowLg,
        }}>
          <Logo size={56} radius={14} />
          <div style={{ color: T.textPrimary, fontSize: 19, fontWeight: 700, marginTop: 16, letterSpacing: -0.4 }}>SaikApp Admin</div>
          <div style={{ color: T.textMuted, fontSize: 13.5, marginTop: 4, marginBottom: 26 }}>Sign in to continue</div>
          <input
            className="ui-field"
            type="password"
            placeholder="Admin password"
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={{
              width: '100%', backgroundColor: T.cardInner,
              border: `1px solid ${error ? T.danger : T.cardBorder}`,
              borderRadius: 10, padding: '12px 14px',
              color: T.textPrimary, fontSize: 14,
              boxSizing: 'border-box', outline: 'none',
            }}
          />
          {error && (
            <div style={{ color: T.danger, fontSize: 12.5, marginTop: 8, alignSelf: 'flex-start', lineHeight: 1.5 }}>{error}</div>
          )}
          <button onClick={handleLogin} disabled={loading} className="ui-btn" style={{
            width: '100%', backgroundColor: T.primary,
            border: 'none', borderRadius: 10, padding: '12px 14px',
            color: '#fff', fontWeight: 600, fontSize: 14,
            cursor: loading ? 'default' : 'pointer', marginTop: 14,
            opacity: loading ? 0.6 : 1,
          }}>{loading ? 'Signing in...' : 'Sign In'}</button>
        </div>
        <div style={{ color: T.textMuted, fontSize: 12, textAlign: 'center', marginTop: 16 }}>
          Restricted access — SaikApp admins only
        </div>
      </div>
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────

// Grouped so eleven flat entries stop reading as one long list. Compliance is
// gone: bike documents are now a filter on the Bikes tab, where the fleet
// already lives. The order is the working day — what needs a decision first,
// then what is running, then content, then settings.
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', short: 'Home', icon: 'dashboard', group: 'Overview' },
  { id: 'analytics', label: 'Analytics', short: 'Stats', icon: 'money', group: 'Overview' },
  { id: 'users', label: 'Users', short: 'Users', icon: 'users', group: 'Approvals' },
  { id: 'bikes', label: 'Bikes', short: 'Bikes', icon: 'bike', group: 'Approvals' },
  { id: 'bookings', label: 'Bookings', short: 'Bookings', icon: 'calendar', group: 'Operations' },
  { id: 'boosts', label: 'Boosts', short: 'Boosts', icon: 'star', group: 'Operations' },
  { id: 'reports', label: 'Reports', short: 'Reports', icon: 'alert', group: 'Operations' },
  { id: 'payouts', label: 'Referral Payouts', short: 'Payouts', icon: 'money', group: 'Operations' },
  { id: 'coupons', label: 'Coupon Codes', short: 'Coupons', icon: 'money', group: 'Operations' },
  { id: 'locations', label: 'Locations', short: 'Places', icon: 'pin', group: 'Content' },
  { id: 'carousel', label: 'Carousel', short: 'Carousel', icon: 'image', group: 'Content' },
  { id: 'broadcast', label: 'Broadcast', short: 'Broadcast', icon: 'megaphone', group: 'Content' },
  { id: 'reviews', label: 'Reviews', short: 'Reviews', icon: 'star', group: 'Content' },
  { id: 'settings', label: 'App Settings', short: 'Settings', icon: 'settings', group: 'System' },
];

/**
 * The tabs that get a slot on the phone's bottom bar.
 *
 * All fourteen used to be on it, in a strip that scrolled sideways: 952px of
 * buttons on a 390px screen. Five were visible, the other nine were off the
 * right-hand edge with nothing to say they existed, so reaching Coupons meant
 * blind-scrolling a 68px-wide icon row. Four and a More sheet is the standard
 * answer because it is the one that fits.
 *
 * These four are the daily ones. Everything else is a few taps away in the
 * sheet, where there is room to group and label it properly — and the counts
 * from the hidden tabs are rolled onto More, so nothing waiting can hide
 * behind it.
 */
const PRIMARY_NAV = ['dashboard', 'users', 'bikes', 'bookings'];

function ThemeToggle({ theme, onToggle, collapsed, compact }) {
  const isDark = theme === 'dark';
  const label = isDark ? 'Light mode' : 'Dark mode';
  if (compact) {
    return (
      <button onClick={onToggle} aria-label={label} title={label} className="ui-btn" style={{
        backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
        borderRadius: 9, padding: '8px 10px', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center',
      }}>
        <Icon name={isDark ? 'sun' : 'moon'} size={17} color={T.textSecondary} />
      </button>
    );
  }
  return (
    <button onClick={onToggle} aria-label={label} title={label} className="ui-navitem" style={{
      width: '100%', backgroundColor: 'transparent', border: 'none',
      color: T.textSecondary, cursor: 'pointer', fontSize: 13, fontWeight: 500,
      padding: collapsed ? '10px 0' : '10px 14px', borderRadius: 9,
      display: 'flex', alignItems: 'center', gap: 11,
      justifyContent: collapsed ? 'center' : 'flex-start',
    }}>
      <Icon name={isDark ? 'sun' : 'moon'} size={18} color={T.textSecondary} />
      {!collapsed && label}
    </button>
  );
}

function Sidebar({ page, onNavigate, onLogout, pendingCounts, collapsed, onToggle, theme, onToggleTheme }) {
  return (
    <div style={{
      width: collapsed ? 72 : 244,
      backgroundColor: T.sidebar,
      borderRight: `1px solid ${T.cardBorder}`,
      display: 'flex', flexDirection: 'column',
      position: 'fixed', height: '100vh',
      transition: 'width 0.18s ease', zIndex: 100, overflow: 'hidden',
    }}>
      <div style={{
        padding: collapsed ? '18px 0' : '18px 16px',
        display: 'flex', alignItems: 'center', gap: 11,
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}>
        <Logo size={34} radius={9} />
        {!collapsed && (
          <div style={{ minWidth: 0 }}>
            <div style={{ color: T.textPrimary, fontWeight: 700, fontSize: 14.5, letterSpacing: -0.2 }}>SaikApp</div>
            <div style={{ color: T.textMuted, fontSize: 11 }}>Admin Panel</div>
          </div>
        )}
      </div>

      <nav style={{ flex: 1, padding: collapsed ? '6px 8px' : '6px 10px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV_ITEMS.map((item, i) => {
          const active = page === item.id;
          const pending = pendingCounts[item.id] || 0;
          // A heading above the first item of each group; skipped when the
          // sidebar is collapsed to icons, where there is nowhere to put it.
          const heading = !collapsed && item.group !== NAV_ITEMS[i - 1]?.group ? (
            <div key={item.group} style={{
              color: T.textMuted, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7,
              textTransform: 'uppercase', padding: i === 0 ? '6px 12px 4px' : '14px 12px 4px',
            }}>{item.group}</div>
          ) : null;
          return (
            <Fragment key={item.id}>
            {heading}
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              title={collapsed ? item.label : undefined}
              className={active ? 'ui-navitem-active' : 'ui-navitem'}
              style={{
                width: '100%', backgroundColor: active ? T.primaryDim : 'transparent',
                border: 'none', borderRadius: 9,
                padding: collapsed ? '11px 0' : '9px 12px',
                color: active ? T.primary : T.textSecondary, cursor: 'pointer',
                fontSize: 13.5, fontWeight: active ? 600 : 500,
                display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 11,
                justifyContent: collapsed ? 'center' : 'flex-start',
                position: 'relative',
              }}
            >
              <Icon name={item.icon} size={18} color={active ? T.primary : T.textMuted} />
              {!collapsed && <span style={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap' }}>{item.label}</span>}
              {pending > 0 && !collapsed && (
                <span style={{
                  backgroundColor: T.warningDim, color: T.warning, border: `1px solid ${T.warningBorder}`,
                  borderRadius: 6, padding: '1px 6px', fontSize: 11, fontWeight: 700,
                }}>{pending}</span>
              )}
              {pending > 0 && collapsed && (
                <span style={{ position: 'absolute', top: 8, right: 14, width: 7, height: 7, borderRadius: 4, backgroundColor: T.warning }} />
              )}
            </button>
            </Fragment>
          );
        })}
      </nav>

      <div style={{ padding: collapsed ? '8px' : '8px 10px', borderTop: `1px solid ${T.divider}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ThemeToggle theme={theme} onToggle={onToggleTheme} collapsed={collapsed} />
        <button onClick={onToggle} className="ui-navitem" style={{
          width: '100%', backgroundColor: 'transparent', border: 'none',
          color: T.textSecondary, cursor: 'pointer', fontSize: 13, fontWeight: 500,
          padding: collapsed ? '10px 0' : '10px 14px', borderRadius: 9,
          display: 'flex', alignItems: 'center', gap: 11,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          <Icon name={collapsed ? 'chevronRight' : 'chevronLeft'} size={18} color={T.textMuted} />
          {!collapsed && 'Collapse'}
        </button>
        <button onClick={onLogout} className="ui-navitem" style={{
          width: '100%', backgroundColor: 'transparent', border: 'none',
          color: T.danger, cursor: 'pointer', fontSize: 13, fontWeight: 500,
          padding: collapsed ? '10px 0' : '10px 14px', borderRadius: 9,
          display: 'flex', alignItems: 'center', gap: 11,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          <Icon name="logout" size={18} color={T.danger} />
          {!collapsed && 'Log out'}
        </button>
      </div>
    </div>
  );
}

function MobileTopBar({ page, onLogout, theme, onToggleTheme }) {
  // The page name, not the product name. The bar is sticky, so this is the one
  // line that is on screen whatever you have scrolled to — spending it on
  // branding left no answer to "which tab am I in" once the title scrolled off.
  const label = NAV_ITEMS.find(n => n.id === page)?.label || 'Admin';
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 90,
      backgroundColor: T.sidebar, borderBottom: `1px solid ${T.cardBorder}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 10, height: 'var(--topbar-h)', boxSizing: 'border-box',
      padding: '11px 16px', paddingTop: 'calc(11px + env(safe-area-inset-top))',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <Logo size={30} radius={8} />
        <h1 style={{
          color: T.textPrimary, fontWeight: 700, fontSize: F.lead, margin: 0,
          letterSpacing: -0.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{label}</h1>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <ThemeToggle theme={theme} onToggle={onToggleTheme} compact />
        <button onClick={onLogout} aria-label="Log out" className="ui-btn" style={{
          backgroundColor: T.dangerDim, border: `1px solid ${T.dangerBorder}`,
          borderRadius: R.sm, padding: '8px 10px', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center',
        }}>
          <Icon name="logout" size={17} color={T.danger} />
        </button>
      </div>
    </div>
  );
}

/**
 * Everything that is not one of the four.
 *
 * Grouped by the same headings the desktop sidebar uses, so the two read as
 * the same map of the panel rather than two different products. The four
 * on the bar are listed here as well: a sheet that answers "what is in this
 * app" has to be complete, and leaving them out invites hunting for a Users
 * entry that is on the bar behind the sheet.
 */
function MoreSheet({ page, onNavigate, pendingCounts, onClose, theme, onToggleTheme, onLogout }) {
  // A sheet that scrolls the page behind it reads as broken on iOS.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const groups = [];
  for (const item of NAV_ITEMS) {
    if (!groups.length || groups[groups.length - 1].name !== item.group) {
      groups.push({ name: item.group, items: [] });
    }
    groups[groups.length - 1].items.push(item);
  }

  return (
    <div
      className="ui-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        backgroundColor: 'var(--backdrop)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
    >
      <div
        className="ui-sheet"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: T.card, borderTop: `1px solid ${T.cardBorder}`,
          borderTopLeftRadius: 18, borderTopRightRadius: 18,
          boxShadow: T.shadowLg, maxHeight: '88vh',
          display: 'flex', flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px 10px', borderBottom: `1px solid ${T.divider}`,
        }}>
          <div style={{ color: T.textPrimary, fontSize: F.lead, fontWeight: 700 }}>All sections</div>
          <button onClick={onClose} aria-label="Close" className="ui-btn" style={{
            background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 6, margin: -6,
          }}>
            <Icon name="x" size={18} color={T.textMuted} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '6px 10px 10px', WebkitOverflowScrolling: 'touch' }}>
          {groups.map(g => (
            <div key={g.name}>
              <div style={{
                color: T.textMuted, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7,
                textTransform: 'uppercase', padding: '14px 8px 6px',
              }}>{g.name}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {g.items.map(item => {
                  const active = page === item.id;
                  const pending = pendingCounts[item.id] || 0;
                  return (
                    <button
                      key={item.id}
                      onClick={() => { onNavigate(item.id); onClose(); }}
                      className="ui-btn"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                        backgroundColor: active ? T.primaryDim : T.cardInner,
                        border: `1px solid ${active ? T.primaryBorder : T.cardBorder}`,
                        borderRadius: R.md, padding: '11px 12px', cursor: 'pointer',
                        color: active ? T.primary : T.textSecondary,
                        fontSize: F.body, fontWeight: active ? 700 : 600, minWidth: 0,
                      }}
                    >
                      <Icon name={item.icon} size={17} color={active ? T.primary : T.textMuted} />
                      <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.label}
                      </span>
                      {pending > 0 && (
                        <span style={{
                          backgroundColor: T.warningDim, color: T.warning,
                          border: `1px solid ${T.warningBorder}`, borderRadius: 6,
                          padding: '1px 6px', fontSize: F.micro, fontWeight: 700, flexShrink: 0,
                        }}>{pending}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div style={{
            display: 'flex', gap: 6, marginTop: 18, paddingTop: 12,
            borderTop: `1px solid ${T.divider}`,
          }}>
            <button onClick={onToggleTheme} className="ui-btn" style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
              borderRadius: R.md, padding: '11px 12px', cursor: 'pointer',
              color: T.textSecondary, fontSize: F.body, fontWeight: 600,
            }}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={16} color={T.textMuted} />
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </button>
            <button onClick={onLogout} className="ui-btn" style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              backgroundColor: T.dangerDim, border: `1px solid ${T.dangerBorder}`,
              borderRadius: R.md, padding: '11px 12px', cursor: 'pointer',
              color: T.danger, fontSize: F.body, fontWeight: 600,
            }}>
              <Icon name="logout" size={16} color={T.danger} />
              Log out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Four tabs and a More.
 *
 * Fixed widths, no sideways scroll: every slot is on screen, which is the
 * whole difference from what this was. More carries the sum of the counts from
 * the tabs it hides, so a report waiting still shows up on the bar.
 */
function BottomNav({ page, onNavigate, pendingCounts, onMore, moreOpen }) {
  const items = PRIMARY_NAV.map(id => NAV_ITEMS.find(n => n.id === id)).filter(Boolean);
  const hiddenPending = NAV_ITEMS
    .filter(n => !PRIMARY_NAV.includes(n.id))
    .reduce((t, n) => t + (pendingCounts[n.id] || 0), 0);
  const moreActive = moreOpen || !PRIMARY_NAV.includes(page);

  const slot = (key, { active, icon, label, pending, onClick }) => (
    <button key={key} onClick={onClick} aria-current={active ? 'page' : undefined} style={{
      flex: 1, minWidth: 0, background: 'transparent', border: 'none', cursor: 'pointer',
      padding: '9px 4px 8px', display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 3, position: 'relative',
      color: active ? T.primary : T.textMuted,
    }}>
      {active && (
        <div style={{
          position: 'absolute', top: 0, left: '28%', right: '28%', height: 2,
          borderRadius: 2, backgroundColor: T.primary,
        }} />
      )}
      <div style={{ position: 'relative' }}>
        <Icon name={icon} size={20} color={active ? T.primary : T.textMuted} />
        {pending > 0 && (
          <span style={{
            position: 'absolute', top: -5, right: -8,
            minWidth: 14, height: 14, borderRadius: 7, padding: '0 3px',
            backgroundColor: T.warning, color: T.sidebar, fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${T.sidebar}`, boxSizing: 'content-box',
          }}>{pending > 99 ? '99+' : pending}</span>
        )}
      </div>
      <span style={{
        fontSize: 10, fontWeight: active ? 700 : 600, whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
      }}>{label}</span>
    </button>
  );

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      backgroundColor: T.sidebar, borderTop: `1px solid ${T.cardBorder}`,
      display: 'flex', paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {items.map(item => slot(item.id, {
        active: page === item.id,
        icon: item.icon,
        label: item.short,
        pending: pendingCounts[item.id] || 0,
        onClick: () => onNavigate(item.id),
      }))}
      {slot('more', {
        active: moreActive,
        icon: 'menu',
        label: 'More',
        pending: hiddenPending,
        onClick: onMore,
      })}
    </div>
  );
}

// ── App ──────────────────────────────────────────────────

/**
 * Which tab the URL is asking for.
 *
 * The panel has no router — it never needed one, since nobody types its
 * addresses. It needs one now only so a status update in the admin Telegram
 * group can land on the thing it is about instead of the dashboard, which
 * means one extra tap per alert, every alert, forever.
 *
 * Anything unrecognised falls back to the dashboard, so an old or mangled
 * link opens the panel rather than a blank page.
 */
function pageFromHash() {
  const id = String(window.location.hash || '').replace(/^#\/?/, '');
  return NAV_ITEMS.some((n) => n.id === id) ? id : 'dashboard';
}

export default function App() {
  const isMobile = useIsMobile();
  const [theme, toggleTheme] = useTheme();
  const [page, setPage] = useState(pageFromHash);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // Back/forward, and a second link pasted into an already-open tab.
  useEffect(() => {
    const onHash = () => setPage(pageFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  useEffect(() => {
    if (pageFromHash() !== page) window.location.hash = page;
    // Switching tab used to keep the scroll position, so leaving Users halfway
    // down a few hundred accounts and tapping Bikes opened Bikes halfway down.
    window.scrollTo({ top: 0 });
  }, [page]);
  const [authLoading, setAuthLoading] = useState(true);
  // Remembered: collapsing the sidebar is a preference about this screen, and
  // having to re-collapse it on every visit is why nobody used it.
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem('saikapp-admin-nav') === 'collapsed'; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem('saikapp-admin-nav', collapsed ? 'collapsed' : 'open'); }
    catch { /* private mode — the panel still works, it just forgets */ }
  }, [collapsed]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [pendingCounts, setPendingCounts] = useState({});
  const [profileId, setProfileId] = useState(null);
  // Stable so the context value does not change identity on every render.
  const openProfile = useCallback((id) => setProfileId(id || null), []);
  const [bookingId, setBookingId] = useState(null);
  const openBooking = useCallback((id) => setBookingId(id || null), []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user && user.email === ADMIN_EMAIL);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    const unsubUsers = onSnapshot(
      query(collection(db, 'users'), where('status', '==', 'pending')),
      snap => setPendingCounts(prev => ({ ...prev, users: snap.size }))
    );
    const unsubBikes = onSnapshot(
      query(collection(db, 'bikes'), where('status', '==', 'pending')),
      snap => setPendingCounts(prev => ({ ...prev, bikes: snap.size }))
    );
    const unsubReports = onSnapshot(
      query(collection(db, 'reports'), where('status', '==', 'open')),
      snap => setPendingCounts(prev => ({ ...prev, reports: snap.size })),
      () => setPendingCounts(prev => ({ ...prev, reports: 0 }))
    );
    const unsubReferrals = onSnapshot(
      collection(db, 'referrals'),
      snap => {
        const pending = snap.docs.filter(d => {
          const r = d.data();
          return r.referrerPayoutStatus === 'pending' || r.refereeBonusStatus === 'pending';
        }).length;
        setPendingCounts(prev => ({ ...prev, payouts: pending }));
      },
      () => setPendingCounts(prev => ({ ...prev, payouts: 0 }))
    );
    // Boost requests with a payment slip waiting to be checked.
    const unsubBoosts = onSnapshot(
      query(collection(db, 'boostRequests'), where('status', '==', 'pending')),
      snap => setPendingCounts(prev => ({ ...prev, boosts: snap.size })),
      () => setPendingCounts(prev => ({ ...prev, boosts: 0 }))
    );
    return () => { unsubUsers(); unsubBikes(); unsubReports(); unsubReferrals(); unsubBoosts(); };
  }, [isLoggedIn]);

  const globalStyle = (
    <style>{`
      ${THEME_CSS}
      * { box-sizing: border-box; }
      html, body { margin: 0; background: var(--bg); }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      ::-webkit-scrollbar { width: 9px; height: 9px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: var(--card-border); border-radius: 5px; }
      ::-webkit-scrollbar-thumb:hover { background: var(--text-muted); }
      button { font-family: inherit; -webkit-tap-highlight-color: transparent; }
      input, textarea { font-family: inherit; }
      input::placeholder, textarea::placeholder { color: var(--text-muted); }

      .ui-btn { transition: filter .14s ease, opacity .14s ease, background-color .14s ease; }
      .ui-btn:hover:not(:disabled) { filter: brightness(1.06); }
      .ui-btn:active:not(:disabled) { transform: translateY(0.5px); }

      .ui-navitem { transition: background-color .14s ease, color .14s ease; }
      .ui-navitem:hover { background-color: var(--card-inner) !important; color: var(--text-primary) !important; }
      .ui-navitem-active { transition: background-color .14s ease; }

      .ui-field { transition: border-color .14s ease, box-shadow .14s ease; }
      .ui-field:focus-within, .ui-field:focus {
        border-color: var(--primary) !important;
        box-shadow: 0 0 0 3px var(--primary-dim) !important;
        outline: none;
      }

      .ui-row { transition: background-color .14s ease, border-color .14s ease; }
      .ui-row:hover { border-color: var(--primary-border) !important; }
      .ui-userlink:hover { text-decoration-style: solid !important; filter: brightness(1.15); }

      .ui-scroll-x { scrollbar-width: none; }
      .ui-scroll-x::-webkit-scrollbar { display: none; }

      /* A row of filters that runs past the right edge of a phone stops dead
         at the clip, which reads as a rendering fault rather than as "there
         is more over here". Fading the last 22px of it says the latter. */
      .ui-fade-x {
        -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 22px), transparent);
        mask-image: linear-gradient(to right, #000 calc(100% - 22px), transparent);
      }

      .ui-overlay { animation: uiFade .14s ease; }
      .ui-modal { animation: uiPop .16s cubic-bezier(.2,.9,.3,1); }
      .ui-sheet { animation: uiSheet .2s cubic-bezier(.2,.9,.3,1); }
      @keyframes uiFade { from { opacity: 0 } to { opacity: 1 } }
      @keyframes uiPop { from { opacity: 0; transform: translateY(6px) scale(.985) } to { opacity: 1; transform: none } }
      @keyframes uiSheet { from { transform: translateY(14px) } to { transform: none } }

      /* DateRangeFilter and SortToggle each carry their own bottom margin,
         which is right when they are stacked down a page and wrong inside the
         Filters row — there it leaves the controls sitting on different
         baselines. Neutralised here rather than threading a prop through. */
      .ui-filters > * { margin-bottom: 0 !important; }

      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; }
      }
    `}</style>
  );

  if (authLoading) return <>{globalStyle}</>;
  if (!isLoggedIn) return <><>{globalStyle}</><Login /></>;

  const pageContent = (
    <PanelErrorBoundary panelKey={page}>
    <Suspense fallback={<div style={{ color: T.textMuted, padding: 40 }}>Loading…</div>}>
      {page === 'dashboard' && <Dashboard onNavigate={setPage} isMobile={isMobile} />}
      {page === 'analytics' && <Analytics isMobile={isMobile} />}
      {page === 'users' && <Users isMobile={isMobile} />}
      {page === 'bikes' && <Bikes isMobile={isMobile} />}
      {page === 'bookings' && <Bookings isMobile={isMobile} />}
      {page === 'payouts' && <Payouts isMobile={isMobile} />}
      {page === 'boosts' && <Boosts isMobile={isMobile} />}
      {page === 'coupons' && <Coupons isMobile={isMobile} />}
      {page === 'locations' && <Locations isMobile={isMobile} />}
      {page === 'reports' && <Reports isMobile={isMobile} />}
      {page === 'carousel' && <Carousel isMobile={isMobile} />}
      {page === 'reviews' && <Reviews isMobile={isMobile} />}
      {page === 'broadcast' && <Broadcast isMobile={isMobile} />}
      {page === 'settings' && <AppSettings isMobile={isMobile} />}
    </Suspense>
    </PanelErrorBoundary>
  );

  const sidebarWidth = collapsed ? 72 : 244;

  // Both layouts get wrapped once at the bottom, so the profile viewer is
  // available from every tab on both desktop and mobile.
  const shell = isMobile ? (
    <div style={{ minHeight: '100vh', backgroundColor: T.bg }}>
      {globalStyle}
      <MobileTopBar page={page} onLogout={() => signOut(auth)} theme={theme} onToggleTheme={toggleTheme} />
      <div style={{ padding: 16, paddingBottom: 'calc(88px + env(safe-area-inset-bottom))' }}>
        {pageContent}
      </div>
      <BottomNav
        page={page}
        onNavigate={setPage}
        pendingCounts={pendingCounts}
        onMore={() => setMoreOpen(o => !o)}
        moreOpen={moreOpen}
      />
      {moreOpen && (
        <MoreSheet
          page={page}
          onNavigate={setPage}
          pendingCounts={pendingCounts}
          onClose={() => setMoreOpen(false)}
          theme={theme}
          onToggleTheme={toggleTheme}
          onLogout={() => signOut(auth)}
        />
      )}
    </div>
  ) : (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: T.bg }}>
      {globalStyle}
      <Sidebar
        page={page}
        onNavigate={setPage}
        onLogout={() => signOut(auth)}
        pendingCounts={pendingCounts}
        collapsed={collapsed}
        onToggle={() => setCollapsed(p => !p)}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
      {/* height, not minHeight. With minHeight the column grows to fit its
          content and overflowY never engages, so it looks like a scroll
          container without being one — and anything inside it that sticks to
          the bottom of the scroller (the settings save bar) has nothing to
          stick to and simply sits at the end of the page. */}
      <div style={{
        marginLeft: sidebarWidth, flex: 1,
        overflowY: 'auto', height: '100vh',
        transition: 'margin-left 0.18s ease',
        maxWidth: `calc(100vw - ${sidebarWidth}px)`,
      }}>
        <div style={{ padding: '32px 36px', maxWidth: 1240, margin: '0 auto' }}>
          {pageContent}
        </div>
      </div>
    </div>
  );

  return (
    <ProfileViewerContext.Provider value={openProfile}>
      <BookingViewerContext.Provider value={openBooking}>
        {shell}
        <UserProfileModal userId={profileId} onClose={() => setProfileId(null)} />
        {/* Above the profile, so opening a booking from someone's history
            lands on top rather than behind it. */}
        <BookingModal bookingId={bookingId} onClose={() => setBookingId(null)} />
      </BookingViewerContext.Provider>
    </ProfileViewerContext.Provider>
  );
}
